import * as React from 'react';
import {styled} from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {BarChart, ChartsLabelMark, PieChart, useDrawingArea} from '@mui/x-charts';
import Stack from "@mui/material/Stack";
import {XYVO} from "@/service/analyze";

const StyledText = styled('text')(({theme}) => ({
    fill: theme.palette.text.primary,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: 14,
}));

function PieCenterLabel({children}: { children: React.ReactNode }) {
    const {width, height, left, top} = useDrawingArea();
    return (
        <StyledText x={left + width / 2} y={top + height / 2}>
            {children}
        </StyledText>
    );
}

export interface Last7dCustomerProps {
    dealsCreator?: XYVO[]
    dealsTrend?: XYVO[]
    leadsCreator?: XYVO[]
    leadsTrend?: XYVO[]
    loading: boolean;
}

const colors = ["#71C0BB", "#F2C078"]

export default function Last7dCustomer({dealsCreator, loading, dealsTrend, leadsCreator, leadsTrend}: Last7dCustomerProps) {
    return (
        <Card variant="outlined" sx={{width: '100%'}}>
            <CardContent>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        近7天新增客户
                    </Typography>
                    <Stack alignItems={'start'} direction={'row'} spacing={2}>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={colors[0]}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>意向客户</Typography>
                        </Stack>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={colors[1]}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>成交客户</Typography>
                        </Stack>
                    </Stack>
                </Stack>

                <Stack direction={'row'} spacing={1}>
                    <PieChart
                        loading={loading}
                        localeText={{loading: "加载中"}}
                        series={[
                            {
                                paddingAngle: 5,
                                innerRadius: 50,
                                outerRadius: 64,
                                data: leadsTrend?.map(item => ({label: item.x, value: item.y, color: '#C7DB9C'})) || [],
                            },
                        ]}
                        height={130}
                        width={130}
                        hideLegend
                    >
                        <PieCenterLabel>意向客户</PieCenterLabel>
                    </PieChart>
                    <BarChart
                        loading={loading}
                        localeText={{loading: "加载中"}}
                        height={180}
                        margin={{left: 0, right: 0, top: 10, bottom: 0}}
                        hideLegend={true}
                        series={[
                            {data: leadsCreator?.map(item => item?.y) || [], label: '意向客户', id: 'leads', color: colors[0]},
                        ]}
                        layout={'horizontal'}
                        xAxis={[{
                            categoryGapRatio: 0.6,
                            reverse: true,
                            disableTicks: true,
                            disableLine: true,
                        }]}
                        yAxis={[{
                            position: 'none',
                            reverse: true,
                            width: 40, disableLine: true, disableTicks: true, data: leadsCreator?.map(item => item.x)
                        }]}
                    />
                    <BarChart
                        loading={loading}
                        localeText={{loading: "加载中"}}
                        height={180}
                        margin={{left: 0, right: 0, top: 10, bottom: 0}}
                        hideLegend={true}
                        series={[
                            {data: dealsCreator?.map(item => item.y) || [], label: '成交客户', id: 'deals', color: colors[1]},
                        ]}
                        layout={'horizontal'}
                        xAxis={[{
                            categoryGapRatio: 0.6,
                            disableTicks: true,
                            disableLine: true,
                        }]}
                        yAxis={[{
                            position: 'none',
                            width: 20, disableLine: true, disableTicks: true, data: dealsCreator?.map(item => item.x)
                        }]}
                    />
                    <PieChart
                        loading={loading}
                        localeText={{loading: "加载中"}}
                        margin={{left: 0, right: 0, top: 0, bottom: 0}}
                        series={[
                            {
                                paddingAngle: 5,
                                innerRadius: 50,
                                outerRadius: 64,
                                data: dealsTrend?.map(item => ({label: item.x, value: item.y, color: '#98D8EF'})) || [],
                            },
                        ]}
                        height={130}
                        width={130}
                        hideLegend
                    > <PieCenterLabel>成交客户</PieCenterLabel>
                    </PieChart>
                </Stack>
            </CardContent>
        </Card>
    );
}
